import React, {useCallback, useContext, useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {UserContext} from "../../hooks/userContext";
import {Button, CircularProgress, TextField, debounce} from "@mui/material";
import Paper from "@mui/material/Paper";
import {useQueryHistory} from "../../queries/order/hooks/useQueryHistory";
import Grid from "@mui/material/Grid";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DatePicker} from "@mui/x-date-pickers";
import Divider from "@mui/material/Divider";
import moment from "moment";
import HistoryGroupItem from "../../components/shared/HistoryGroupItem/HistoryGroupItem";
import {OrderUserType} from "../../constants/order";
import {
  countGroupedOrders,
  exportOrderHistoryToExcel,
  exportOrderHistoryToPdf,
} from "../../utils/exportOrderHistoryExcel";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

interface IBuyHistoryProps {
  isAdmin?: boolean;
}

export const BuyHistory:React.FunctionComponent<IBuyHistoryProps> = ({ isAdmin }) => {
  const { id }: any = useContext(UserContext);
  const [fromDate, setFromDate] = useState<any>(moment().subtract(2, 'weeks'));
  const [toDate, setToDate] = useState<any>(moment());
  const [orders, setOrders] = useState<Record<string, Record<string, any[]>>>({});
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const [getHistory, {data, loading: queryLoading, refetch }] = useQueryHistory({
    variables: isAdmin ? { orderBy: [ { createdAt: "desc" } ] } : { 
      where: { buyerUserId: { equals: +id } },
      orderBy: [ { createdAt: "desc" } ] },
    fetchPolicy: 'no-cache',
    pollInterval: 5000
  });

  useEffect(() => {
    const groupedOrders: Record<string, Record<string, any[]>> = {};

    // Sort orders: legacy (>8 digits) by createdAt desc, new (≤8 digits) by orderNumber desc
    const sortedOrders = [...(data?.findManyOrder ?? [])].sort((a: any, b: any) => {
      const aOrderNumStr = String(a.orderNumber);
      const bOrderNumStr = String(b.orderNumber);
      const aIsLegacy = aOrderNumStr.length > 8;
      const bIsLegacy = bOrderNumStr.length > 8;

      if (aIsLegacy && bIsLegacy) {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else if (!aIsLegacy && !bIsLegacy) {
        const aNum = Number(aOrderNumStr);
        const bNum = Number(bOrderNumStr);
        if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
          return bOrderNumStr.localeCompare(aOrderNumStr);
        }
        return bNum - aNum;
      } else {
        // Put new orders (≤8 digits) first, then legacy orders
        return aIsLegacy ? 1 : -1;
      }
    });

    for (const order of sortedOrders) {
      // Prefix orderNumber with "o_" to prevent integer-index coercion
      // "o_10000000" is not treated as an array index, preserving insertion order
      const orderKey = `o_${order.orderNumber}`;
      const sellerStatusKey = `${order.Seller.id}_${order.status === 1 ? 'a' : order.status === 2 ? 'b' : 'c'}`;

      if (!groupedOrders[orderKey]) {
        groupedOrders[orderKey] = {};
      }

      if (!groupedOrders[orderKey][sellerStatusKey]) {
        groupedOrders[orderKey][sellerStatusKey] = [];
      }

      if (order) {
        groupedOrders[orderKey][sellerStatusKey].push(order);
      }
    }

    setOrders(groupedOrders);

  },[data?.findManyOrder]);

  useEffect(() => {
    let andQuery: any = isAdmin ? [] : [
      {buyerUserId: {equals: +id}},
    ];

    if (searchInput) {
      isAdmin ?
        andQuery.push(
          {
            OR: [
              {
                orderNumber: {
                  contains: searchInput,
                  mode: 'insensitive'
                },
              },
              {
                Seller: { is: {
                  legalName: {
                    contains: searchInput,
                    mode: 'insensitive'
                  }
                }}
              }
            ]
          }
        )
      : andQuery.push(
        {
          orderNumber: {
            contains: searchInput,
            mode: 'insensitive'
          },
        }
      )
    }

    if (fromDate) {
      const dateStr = fromDate.format('YYYY-MM-DD');
      andQuery.push({createdAt: { gte: moment(`${dateStr} 00:00:00`).utc().format('YYYY-MM-DD HH:mm:ss') }});
    }

    if (toDate) {
      const dateStr = toDate.format('YYYY-MM-DD');
      andQuery.push({createdAt: { lte: moment(`${dateStr} 23:59:59`).utc().format('YYYY-MM-DD HH:mm:ss')}});
    }

    getHistory({
      variables: {
        where: { AND: andQuery },
        orderBy: [ { createdAt: "desc" } ] },
      fetchPolicy: 'no-cache',
      pollInterval: 5000
    })
  }, [id, getHistory, fromDate, toDate]);

  const search = useCallback(debounce(async (value: string) => {
    setLoading(true);
    let andQuery: any = isAdmin ? [] : [
      {buyerUserId: {equals: +id}},
    ];

    if (value) {
      isAdmin ?
        andQuery.push(
          {
            OR: [
              {
                orderNumber: {
                  contains: value,
                  mode: 'insensitive'
                },
              },
              {
                Seller: { is: {
                  legalName: {
                    contains: value,
                    mode: 'insensitive'
                  }
                }}
              }
            ]
          }
        )
      : andQuery.push(
        {
          orderNumber: {
            contains: value,
            mode: 'insensitive'
          },
        }
      )
    }

    if (fromDate) {
      const dateStr = fromDate.format('YYYY-MM-DD');
      andQuery.push({createdAt: { gte: moment(`${dateStr} 00:00:00`).utc().format('YYYY-MM-DD HH:mm:ss') }});
    }

    if (toDate) {
      const dateStr = toDate.format('YYYY-MM-DD');
      andQuery.push({createdAt: { lte: moment(`${dateStr} 23:59:59`).utc().format('YYYY-MM-DD HH:mm:ss')}});
    }

    await refetch({
      where: { AND: andQuery },
      orderBy: [ { createdAt: "desc" } ],
    });
    setLoading(false);
  }, 1000), [fromDate, toDate]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
    search(value);
  }

  const handleExport = () => {
    exportOrderHistoryToExcel({
      orders,
      fromDate,
      toDate,
      mode: 'buy',
      isAdmin,
    });
  };

  const handleExportPdf = () => {
    exportOrderHistoryToPdf({
      orders,
      fromDate,
      toDate,
      mode: 'buy',
      isAdmin,
    });
  };

  const canExport = !queryLoading && !loading && countGroupedOrders(orders) > 0;

  return (
    <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', minHeight: '350px' }}>
      <Typography component="h1" variant="h5">
        {isAdmin ? 'Sales' : 'Buy History'}
      </Typography>
      <Grid container sx={{mt: 2, mb: 2}} spacing={2}>
        <Grid item xs={12} sm={12} md={4} xl={2}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={fromDate}
              onChange={(date: any) => setFromDate(date)}
              format={"MM/DD/YYYY"}
              label="From date"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={12} md={4} xl={2}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={toDate}
              onChange={(date: any) => setToDate(date)}
              format={"MM/DD/YYYY"}
              label="To date"
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Grid container sx={{mb: 2}} spacing={2} alignItems="center">
        <Grid item xs={12} sm={12} md={8} xl={4}>
          <TextField
            type="text"
            placeholder={`Search by ${isAdmin ? 'Seller or ' : ''}Order Number...`}
            value={searchInput}
            onChange={(event: any) => handleSearch(event.target.value)}
            fullWidth
            sx={{ minHeight: '30px', minWidth:'100%' }}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4} xl={8} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleExportPdf}
            disabled={!canExport}
            startIcon={<PictureAsPdfIcon />}
          >
            Export to PDF
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={!canExport}
            startIcon={<DescriptionIcon />}
          >
            Export to Excel
          </Button>
        </Grid>
      </Grid>
      <Divider sx={{borderWidth: '1px'}} />
      {(queryLoading || loading) ? (
        <CircularProgress sx={{position: 'absolute', left: 'calc(150px + 50%)', top: '50%'}} color="inherit" />
      ) : (
        <>
          {data?.findManyOrder.length === 0 ? (
            <Box sx={{ mt: 10 }}>
              <Typography component="h1" variant="h5" sx={{textAlign: 'center'}}>No data</Typography>
            </Box>
          ) : (
            <HistoryGroupItem
              orders={orders}
              refetch={refetch}
              userType={OrderUserType.buyer}
              refetchLoading={queryLoading}
              isAdmin={isAdmin}
            />
          )}
        </>
      )}
    </Paper>
  );
}

export default BuyHistory;